import { consume } from '@lit/context';
import { LitElement, html, css, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { sourceContext } from '../../../contexts/source';
import { deliveryMethodContext } from '../../../contexts/deliveryMethod';
import { getAnalyticPlanDescription } from '../../../util/analytics';
import {
    enrollPlanHandler,
    recommendPlanHandler,
    keepPlanHandler,
    selectNewPlanHandler,
    BackendPlan,
} from '../../../util/plans';
import { SOURCE, sourcesToRecommend, isEmbeddedAgentEmailDelivery } from '../../../util/source';
import { inputResponseContext } from '../../../contexts/inputResponse';
import { BackendFormResponse } from '../../../util/api';
import { readOnlyContext } from '../../../contexts/readOnly';

@customElement('rq-mobile-details-nav')
export class MobileDetailsNav extends LitElement {
    @consume({ context: sourceContext })
    source!: SOURCE;

    @consume({ context: deliveryMethodContext })
    deliveryMethod?: string;

    @property()
    plan!: BackendPlan;

    @consume({ context: inputResponseContext, subscribe: true })
    @state()
    inputResponse!: BackendFormResponse;

    @consume({ context: readOnlyContext, subscribe: true })
    @state()
    readOnly = false;

    @property({ type: Function }) onBackToPlanResults!: () => void;
    static styles = css`
        :host {
            position: fixed;
            bottom: 0;
            width: 100%;
        }
    `;

    get enrollButton() {
        const handler = () => enrollPlanHandler(this.plan.planId, this.plan.qhpId, this.inputResponse);
        const analyticTag = `rate-quote:${this.source}:u65:plan-details-${getAnalyticPlanDescription(this.plan)}:enroll:button-filled`;

        return html` <lh-button
            @click=${handler}
            analytics-id=${analyticTag}
            small
            full-width
            accessible-label
            .disabled=${this.readOnly}
            variant="filled"
        >
            Enroll
        </lh-button>`;
    }

    get recommendButton() {
        const handler = () => {
            recommendPlanHandler(this.plan.planId);
            this.onBackToPlanResults();
        };
        const analyticTag = `rate-quote:${this.source}:u65:plan-details-${getAnalyticPlanDescription(this.plan)}:recommend:button-filled`;

        return html` <lh-button
            @click=${handler}
            analytics-id=${analyticTag}
            small
            full-width
            .disabled=${this.readOnly}
            accessible-label
            variant="filled"
        >
            Recommend
        </lh-button>`;
    }

    get keepPlanButton() {
        const handler = () => keepPlanHandler(this.plan.planCode);
        const analyticTag = `rate-quote:${this.source}:u65:plan-details-${getAnalyticPlanDescription(this.plan)}:keep:button-filled`;

        return html` <lh-button
            @click=${handler}
            analytics-id=${analyticTag}
            small
            full-width
            accessible-label
            variant="filled"
        >
            Keep
        </lh-button>`;
    }

    get selectNewButton() {
        const handler = () => selectNewPlanHandler(this.plan.planCode);
        const analyticTag = `rate-quote:${this.source}:u65:plan-details-${getAnalyticPlanDescription(this.plan)}:select-new:button-filled`;
        return html` <lh-button
            @click=${handler}
            analytics-id=${analyticTag}
            small
            full-width
            accessible-label
            variant="filled"
        >
            Select
        </lh-button>`;
    }

    get originalPlan() {
        return this.plan.planCode === this.inputResponse.currentPlan;
    }

    get actionButton() {
        // When an embedded agent chooses to email the quote to their client, act like SOURCE.EMAIL
        // and always recommend (the embedded-agent quote request isn't flagged as producer context).
        if (isEmbeddedAgentEmailDelivery(this.source, this.deliveryMethod)) {
            return this.recommendButton;
        }
        return choose(
            this.source,
            [
                ...sourcesToRecommend.map((s): [SOURCE, () => TemplateResult] => [
                    s,
                    () =>
                        this.inputResponse?.context?.toLowerCase() === 'shopper'
                            ? this.enrollButton
                            : this.recommendButton,
                ]),
                [SOURCE.DIRECT_SHOPPER, () => this.enrollButton],
                [SOURCE.PABOL, () => this.enrollButton],
                [SOURCE.EMBEDDED_AGENT, () => this.enrollButton],
                [SOURCE.SALESFORCE, () => this.enrollButton],
                [
                    SOURCE.CHANGE_APP,
                    () => {
                        if (this.originalPlan) {
                            return this.keepPlanButton;
                        }
                        return this.selectNewButton;
                    },
                ],
            ],
            () => this.enrollButton
        );
    }

    render() {
        return html`
            <lh-container sticky theme="secondary-dark" content-width="xxl" padding-top="md" padding-bottom="md">
                <lh-grid>
                    <lh-grid-item span-mobile="6">
                        <lh-button
                            size="sm"
                            full-width
                            accessible-label
                            variant="outlined"
                            @click=${this.onBackToPlanResults}
                        >
                            Back
                        </lh-button></lh-grid-item
                    >
                    <lh-grid-item span-mobile="6"> ${this.actionButton}</lh-grid-item>
                </lh-grid>
            </lh-container>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-mobile-details-nav': MobileDetailsNav;
    }
}
